import { useLocalizedCountries } from '@/hooks/useLocalizedCountries';
import { useSpecialtyDisplayName } from '@/hooks/useSpecialtyDisplayName';
import { useTutorial } from '@/hooks/useTutorial';
import { getInitials } from '@/utils/getInitials';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  getAvatarColor,
} from '@heidi/design-system/avatar';
import { Card, CardContent, CardHeader } from '@heidi/design-system/card';
import { TypographyCaption, TypographyPBold } from '@heidi/design-system/typography';
import { TemplatePages } from '@heidi/schema/ml-scribe';
import type { TemplateBasicInfo } from '@heidi/templates/contracts';
import { redirectToAPSE } from '@heidi/utils';
import { countries } from '@heidi/utils/countries';
import { BadgeCheck, UserRound } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  communityFiltersMessages,
  getTemplateCategoryFilterLabel,
  isTemplateCategoryFilterKey,
} from './communityFilters.messages';

const TemplateCard = ({ template }: { template: TemplateBasicInfo }) => {
  const router = useRouter();
  const intl = useIntl();
  const { onNextPage, tutorialStepCurrentPage } = useTutorial();
  const getSpecialtyDisplayName = useSpecialtyDisplayName();
  const { getLocalizedNameFromEnglish } = useLocalizedCountries();
  const shouldClickTemplateCard =
    tutorialStepCurrentPage?.enum === TemplatePages.BROWSE_TEMPLATES;

  const authorProfilePictureUrl = redirectToAPSE(template?.author_profile_picture);

  const trimmedAuthorName = template.author_name.trim();
  const isAnonymousAuthor = trimmedAuthorName === '';
  // Single-initial fallback: take the first character of the title-stripped
  // initials (e.g. "Dr Jane Smith" -> "JS" -> "J") so titles never show up
  // as the lead character.
  const avatarInitials = isAnonymousAuthor
    ? 'A'
    : getInitials(trimmedAuthorName).charAt(0);
  // Anonymous is pinned to neutral; named authors get one of the four palette
  // colors deterministically hashed from their name.
  const avatarColor = isAnonymousAuthor ? 'neutral' : getAvatarColor(trimmedAuthorName);
  // Screen-reader label that names the author (or "Anonymous") rather than a
  // generic "profile picture" string.
  const avatarAltText = isAnonymousAuthor
    ? intl.formatMessage({ defaultMessage: 'Anonymous', id: 'LXxsbkQcTH' })
    : trimmedAuthorName;

  const templateCountryCode = useMemo(() => {
    return countries
      .find((country) => country.name.toLowerCase() === template.country.toLowerCase())
      ?.countryCode?.toUpperCase();
  }, [template.country]);

  // Get localized country name for accessibility
  const localizedCountryName = useMemo(() => {
    return getLocalizedNameFromEnglish(template.country);
  }, [template.country, getLocalizedNameFromEnglish]);

  const categoryLabel = isTemplateCategoryFilterKey(template.template_category)
    ? getTemplateCategoryFilterLabel(intl, template.template_category)
    : intl.formatMessage(communityFiltersMessages.document);

  return (
    <Card
      data-testid="community-template-card"
      data-template-id={template.template_id}
      data-template-category={template.template_category}
      className="flex w-full cursor-pointer flex-col justify-between gap-4 transition hover:border-border-hover"
      onClick={() => {
        void router.push(`/templates/${template.url_path}`);
        if (shouldClickTemplateCard) {
          onNextPage();
        }
      }}
    >
      <CardHeader className="space-y-0 p-4 pb-0">
        <TypographyCaption className="text-text-secondary">
          {categoryLabel}
        </TypographyCaption>
        <TypographyPBold data-testid="community-template-card-title">
          {template.title}
        </TypographyPBold>
      </CardHeader>
      <CardContent className="flex justify-between gap-4 p-4 pt-0">
        <div className="flex items-center gap-3">
          <div className="relative h-fit">
            <Avatar size="md" color={avatarColor}>
              <AvatarImage
                src={authorProfilePictureUrl ?? undefined}
                alt={avatarAltText}
                className="object-cover"
              />
              <AvatarFallback>{avatarInitials}</AvatarFallback>
            </Avatar>

            {templateCountryCode && (
              <Image
                src={`/country-flags/3x2/${templateCountryCode}.svg`}
                alt={localizedCountryName}
                width={21}
                height={14}
                className="absolute bottom-0 right-[calc(50%-10.5px)] translate-x-1/2 rounded-sm border border-border-disabled"
              />
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <div className="relative">
                <TypographyCaption>
                  {trimmedAuthorName || (
                    <FormattedMessage defaultMessage="Anonymous" id="LXxsbkQcTH" />
                  )}
                </TypographyCaption>
              </div>
              <BadgeCheck
                className="fill-validation-info text-text-primary-invert"
                size={16}
              />
            </div>
            <TypographyCaption className="text-text-secondary">
              {template.specialty
                ? getSpecialtyDisplayName(template.specialty)
                : intl.formatMessage({
                    defaultMessage: 'Not specified',
                    id: 'LoQQXOhBRd',
                    description:
                      'Fallback caption for medical specialty on a community template card when the template creator has no specialty set',
                  })}
            </TypographyCaption>
          </div>
        </div>
        <div className="flex items-center gap-1 self-end">
          <UserRound className="text-text-secondary" size={16} />
          <TypographyCaption className="text-text-secondary">
            {template.imports_count}
          </TypographyCaption>
        </div>
      </CardContent>
    </Card>
  );
};

export default TemplateCard;
